import React, { useEffect, useState } from "react";
import { trackEvent } from "@leafly-com/web-utils";

import OpenInNewIcon from "@/components/Icons/open_in_new.svg";

interface DoctorAffiliateAd {
  backgroundColor: string;
  bodyHTML: string;
  clickthroughURL: string;
  clientName: string;
  logoImageUrl: string;
  title: string;
  url: string;
}

/**
 * As of summer 2025, we're expanding the existing Leafwell Doctor Finder ad
 * to a rotating placement between Leafwell and NuggMD.
 *
 * These placements are hardcoded while we determine if:
 * 1) There's customer interest in the placement
 * 2) There are enough impressions and clicks to support more than one ad
 *
 * The intention is NOT to expand this hardcoded list indefinitely. If we need
 * to include more than 2 ads in this placement, we should consider expanding
 * our existing Inventory Manager to support share-of-voice placements and
 * moving these ad units there.
 */
const ads: DoctorAffiliateAd[] = [
  {
    backgroundColor: "#333333",
    bodyHTML: 'Code: <span class="font-bold"> LEAFLY20</span>',
    clickthroughURL:
      "https://leafwell.com/medical-card?utm_source=leafly&utm_medium=doctorad",
    clientName: "Leafwell",
    logoImageUrl:
      "https://leafly-public.imgix.net/web-web/finder/ef1a149a770e_Leafwell_FinderLogo.svg",
    title: "Save 20% on your medical marijuana card with Leafwell",
    url: "leafwell.com",
  },
  {
    backgroundColor: "#103764",
    bodyHTML: 'Code: <span class="font-bold">LEAFLY21</span>',
    clickthroughURL:
      "https://www.nuggmd.com/leafly23?utm_source=leafly&utm_campaign=leafly23",
    clientName: "NuggMD",
    logoImageUrl:
      "https://leafly-public.imgix.net/web-web/finder/73e653f24492_NuggMD_FinderLogo.png",
    title: "Save 21% on your medical marijuana card with NuggMD",
    url: "nuggmd.com",
  },
];

const DoctorAd = () => {
  const [ad, setAd] = useState<DoctorAffiliateAd | null>(null);

  useEffect(() => {
    const adIndex = Math.floor(Math.random() * ads.length);
    const selectedAd = ads[adIndex];

    if (selectedAd) {
      trackEvent(
        `${selectedAd.clientName} Banner`,
        "impression",
        "doctor finder",
      );
      setAd(selectedAd);
    }
  }, []);

  if (!ad) {
    return null;
  }

  return (
    <a
      href={ad.clickthroughURL}
      rel="noreferrer"
      target="_blank"
      onClick={() =>
        trackEvent(`${ad.clientName} Banner`, "click", "doctor finder")
      }
    >
      <div
        className="rounded p-lg text-white flex flex-col justify-between cursor-pointer my-lg"
        style={{ backgroundColor: ad.backgroundColor }}
      >
        <h3 className="mb-sm" data-testid="doctor-finder-ad-headline">
          {ad.title}
        </h3>
        <div dangerouslySetInnerHTML={{ __html: ad.bodyHTML }} />
        <div className="flex flex-row items-center justify-between">
          <div className="font-bold text-xs flex flex-row items-center ">
            At <span className="underline mx-xs ">{ad.url}</span>
            <OpenInNewIcon width="14" height="14" />
          </div>
          <img
            src={ad.logoImageUrl}
            alt=""
            className="max-w-half max-h-6 object-contain"
          />
        </div>
      </div>
    </a>
  );
};

export default DoctorAd;
